import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  ADVANCED_ROLES_CONFIG, 
  convertToAdvancedPermissions, 
  convertToLegacyPermissions,
  MIGRATION_STRATEGY 
} from '../config/advancedRolesConfig';

/**
 * Service pour la gestion de la nouvelle table advanced_roles
 */
export class AdvancedRolesService {
  static COLLECTION_NAME = ADVANCED_ROLES_CONFIG.COLLECTION_NAME;

  /**
   * Initialise la nouvelle table avec les rôles par défaut
   * @param {string} userId - ID de l'utilisateur qui effectue l'initialisation
   * @returns {Promise<void>}
   */
  static async initializeAdvancedRoles(userId) {
    try {
      const rolesSnapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      
      if (rolesSnapshot.empty) {
        console.log('Initialisation des rôles avancés...');
        
        for (const role of Object.values(ADVANCED_ROLES_CONFIG.DEFAULT_ROLES)) {
          await this.createRole({
            ...role,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: userId,
            updatedBy: userId,
            isActive: true,
            version: 1
          });
        }
        
        console.log('Rôles avancés initialisés avec succès');
      }
    } catch (error) {
      console.error('Erreur lors de l\'initialisation des rôles avancés:', error);
      throw error;
    }
  }

  /**
   * Migre les rôles depuis l'ancienne collection vers la nouvelle
   * @param {string} userId - ID de l'utilisateur qui effectue la migration
   * @returns {Promise<void>}
   */
  static async migrateFromLegacy(userId) {
    try {
      console.log('Début de la migration des rôles...');
      
      // Récupérer les anciens rôles
      const legacyRolesSnapshot = await getDocs(collection(db, 'roles'));
      const legacyRoles = legacyRolesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      for (const legacyRole of legacyRoles) {
        const newRoleId = MIGRATION_STRATEGY.OLD_TO_NEW[legacyRole.id] || legacyRole.id;
        
        // Vérifier si le rôle existe déjà dans la nouvelle table
        const existingRole = await this.getRole(newRoleId);
        
        if (!existingRole) {
          // Convertir les permissions vers le nouveau format
          const advancedPermissions = convertToAdvancedPermissions(legacyRole.permissions || {});
          
          // Créer le nouveau rôle
          await this.createRole({
            id: newRoleId,
            name: legacyRole.name || newRoleId,
            description: legacyRole.description || '',
            level: this.getDefaultLevel(newRoleId),
            isSystem: ['admin', 'user', 'guest'].includes(newRoleId),
            isActive: true,
            permissions: advancedPermissions,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            createdBy: userId,
            updatedBy: userId,
            version: 1
          });
          
          console.log(`Rôle migré: ${legacyRole.id} -> ${newRoleId}`);
        }
      }
      
      console.log('Migration des rôles terminée avec succès');
    } catch (error) {
      console.error('Erreur lors de la migration des rôles:', error);
      throw error;
    }
  }

  /**
   * Récupère tous les rôles avancés
   * @returns {Promise<Array>} Liste des rôles
   */
  static async getAllRoles() {
    try {
      const snapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Erreur lors de la récupération des rôles:', error);
      throw error;
    }
  }

  /**
   * Récupère un rôle spécifique
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Object|null>} Le rôle ou null si non trouvé
   */
  static async getRole(roleId) {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, roleId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error(`Erreur lors de la récupération du rôle ${roleId}:`, error);
      throw error;
    }
  }

  /**
   * Crée un nouveau rôle
   * @param {Object} roleData - Données du rôle
   * @returns {Promise<void>}
   */
  static async createRole(roleData) {
    try {
      const { id, ...data } = roleData;
      await setDoc(doc(db, this.COLLECTION_NAME, id), {
        ...data,
        createdAt: data.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur lors de la création du rôle:', error);
      throw error;
    }
  }

  /**
   * Met à jour un rôle existant
   * @param {string} roleId - ID du rôle
   * @param {Object} updates - Données à mettre à jour
   * @param {string} userId - ID de l'utilisateur qui effectue la modification
   * @returns {Promise<void>}
   */
  static async updateRole(roleId, updates, userId) {
    try {
      await updateDoc(doc(db, this.COLLECTION_NAME, roleId), {
        ...updates,
        updatedAt: serverTimestamp(),
        updatedBy: userId
      });
    } catch (error) {
      console.error(`Erreur lors de la mise à jour du rôle ${roleId}:`, error);
      throw error;
    }
  }

  /**
   * Supprime un rôle
   * @param {string} roleId - ID du rôle
   * @returns {Promise<void>}
   */
  static async deleteRole(roleId) {
    try {
      // Vérifier si c'est un rôle système qui ne peut pas être supprimé
      const role = await this.getRole(roleId);
      if (role && role.isSystem) {
        throw new Error('Impossible de supprimer un rôle système');
      }
      
      await deleteDoc(doc(db, this.COLLECTION_NAME, roleId));
    } catch (error) {
      console.error(`Erreur lors de la suppression du rôle ${roleId}:`, error);
      throw error;
    }
  }

  /**
   * Récupère les permissions d'un utilisateur basées sur son rôle
   * @param {string} roleId - ID du rôle
   * @returns {Promise<Object>} Permissions de l'utilisateur
   */
  static async getUserPermissions(roleId) {
    try {
      const role = await this.getRole(roleId);
      if (!role || !role.isActive) {
        return {};
      }
      
      return role.permissions || {};
    } catch (error) {
      console.error(`Erreur lors de la récupération des permissions pour le rôle ${roleId}:`, error);
      return {};
    }
  }

  /**
   * Vérifie si un utilisateur a une permission spécifique
   * @param {string} roleId - ID du rôle
   * @param {string} resource - Ressource concernée
   * @param {string} action - Action demandée
   * @returns {Promise<boolean>} True si l'utilisateur a la permission
   */
  static async hasPermission(roleId, resource, action) {
    try {
      const permissions = await this.getUserPermissions(roleId);
      const resourcePermissions = permissions[resource];
      
      if (!resourcePermissions) {
        return false;
      }
      
      // Gestion des permissions hiérarchiques
      switch (action) {
        case 'write':
        case 'create':
        case 'delete':
          // Pour write/create/delete, vérifier aussi read
          return resourcePermissions.read && resourcePermissions[action];
        case 'manage':
          // Manage nécessite toutes les permissions de base
          return resourcePermissions.read && 
                 resourcePermissions.write && 
                 resourcePermissions.create && 
                 resourcePermissions.delete;
        default:
          return resourcePermissions[action] || false;
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des permissions:', error);
      return false;
    }
  }

  /**
   * Retourne le niveau par défaut d'un rôle basé sur son ID
   * @param {string} roleId - ID du rôle
   * @returns {number} Niveau hiérarchique
   */
  static getDefaultLevel(roleId) {
    const defaultLevels = {
      'super_admin': 100,
      'admin': 90,
      'manager': 80,
      'technician': 70,
      'commercial': 60,
      'user': 50,
      'guest': 10
    };
    
    return defaultLevels[roleId] || 50;
  }

  /**
   * Exporte la configuration des rôles au format JSON
   * @returns {Promise<string>} JSON stringifié
   */
  static async exportRoles() {
    try {
      const roles = await this.getAllRoles();
      return JSON.stringify({
        version: '2.0',
        exportDate: new Date().toISOString(),
        schema: 'advanced_roles',
        roles: roles.map(role => ({
          id: role.id,
          name: role.name,
          description: role.description,
          level: role.level,
          isSystem: role.isSystem,
          isActive: role.isActive,
          permissions: role.permissions,
          version: role.version
        }))
      }, null, 2);
    } catch (error) {
      console.error('Erreur lors de l\'export des rôles:', error);
      throw error;
    }
  }

  /**
   * Importe une configuration de rôles
   * @param {Object} importData - Données d'import
   * @param {string} userId - ID de l'utilisateur qui importe
   * @returns {Promise<void>}
   */
  static async importRoles(importData, userId) {
    try {
      if (!importData.roles || !Array.isArray(importData.roles)) {
        throw new Error('Format d\'import invalide');
      }

      for (const roleData of importData.roles) {
        await this.createRole({
          ...roleData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: userId,
          updatedBy: userId
        });
      }
    } catch (error) {
      console.error('Erreur lors de l\'import des rôles:', error);
      throw error;
    }
  }
}

export default AdvancedRolesService;
